import { Matrix4, Quaternion, Vector3 } from 'three';

import type { CartesianPose } from '@sb/geometry';

/**
 * Axis remapping from target-frame (X = approach) to display-frame:
 *   display X (red)   = pose +Y  (in-plane)
 *   display Y (green) = pose -Z  (in-plane)
 *   display Z (blue)  = pose -X  (outward normal)
 *
 * Shared between the axis overlays (3D visualization) and poseToDisplayFrame
 * (offset reference frame) so both stay in sync.
 */
export const DISPLAY_AXES = {
  x: new Vector3(0, 1, 0),
  y: new Vector3(0, 0, -1),
  z: new Vector3(-1, 0, 0),
} as const;

/**
 * Quaternion form of the DISPLAY_AXES remapping (det = +1, right-handed).
 * Multiplying a pose quaternion by this gives the display frame orientation.
 */
export const DISPLAY_REMAP_QUATERNION = new Quaternion().setFromRotationMatrix(
  new Matrix4().makeBasis(DISPLAY_AXES.x, DISPLAY_AXES.y, DISPLAY_AXES.z),
);

/**
 * Convert a detection pose (target-frame: X = approach into surface) to
 * the display coordinate frame used by the axis overlays for visualization.
 *
 * This ensures that offsets entered in the Results tab move the detection
 * along the same axes shown in the 3D visualizer.
 */
export function poseToDisplayFrame(
  detectionPose: CartesianPose,
): CartesianPose {
  const poseQ = new Quaternion(
    detectionPose.i,
    detectionPose.j,
    detectionPose.k,
    detectionPose.w,
  );

  const displayQ = poseQ.multiply(DISPLAY_REMAP_QUATERNION);

  return {
    x: detectionPose.x,
    y: detectionPose.y,
    z: detectionPose.z,
    i: displayQ.x,
    j: displayQ.y,
    k: displayQ.z,
    w: displayQ.w,
  };
}
